import React, { useState } from "react";
import { Button, FloatButton, Form, Input, Modal } from "antd";
import { BugOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { api } from "../utils/apiClient";
import { useAuth } from "../context/AuthContext";
import { appMessage as message } from "../utils/appMessage";
import { wasNotified } from "../utils/notifiedError";

const createTelemetry = (user) => ({
  currentUrl: window.location.href,
  path: window.location.pathname,
  userId: user?.id || null,
  username: user?.username || null,
  email: user?.email || null,
  role: user?.role || null,
  userAgent: navigator.userAgent,
  language: navigator.language,
  platform: navigator.platform,
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  screen: `${window.screen.width}x${window.screen.height}`,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  referrer: document.referrer || null,
  submittedAt: new Date().toISOString(),
});

export default function GitlabTicketFloatButton() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { user } = useAuth();

  const onOpen = () => setOpen(true);

  const onClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const onSubmit = async (values) => {
    const payload = {
      title: values.title,
      description: values.description,
      telemetry: createTelemetry(user),
    };

    setSubmitting(true);
    try {
      await api.post("/api/gitlab/createIssue", payload);
      message.success(t("gitlabTicket.messages.createSuccess"));

      onClose();
    } catch (error) {
      const errMessage =
        error?.response?.data?.error ||
        error?.message ||
        t("gitlabTicket.messages.createFailed");
      if (!wasNotified(error)) message.error(errMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <FloatButton
        icon={<BugOutlined />}
        type="primary"
        tooltip={t("gitlabTicket.tooltip")}
        onClick={onOpen}
      />

      <Modal
        title={t("gitlabTicket.modal.title")}
        open={open}
        onCancel={onClose}
        width={640}
        footer={null}
      >
        <Form layout="vertical" form={form} onFinish={onSubmit}>
          <Form.Item
            label={t("gitlabTicket.form.titleLabel")}
            name="title"
            rules={[
              {
                required: true,
                message: t("gitlabTicket.form.validation.titleRequired"),
              },
            ]}
          >
            <Input placeholder={t("gitlabTicket.form.titlePlaceholder")} />
          </Form.Item>

          <Form.Item
            label={t("gitlabTicket.form.descriptionLabel")}
            name="description"
            rules={[
              {
                required: true,
                message: t("gitlabTicket.form.validation.descriptionRequired"),
              },
            ]}
          >
            <Input.TextArea
              rows={6}
              placeholder={t("gitlabTicket.form.descriptionPlaceholder")}
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
            <Button onClick={onClose}>{t("common.actions.cancel")}</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              style={{ marginLeft: 8 }}
            >
              {t("gitlabTicket.form.submit")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
